import React from "react";
import { observer } from "mobx-react";
import { computed, observable } from "mobx";
import { withRouter, RouteComponentProps } from "react-router";
import InfiniteScroll from "react-infinite-scroll-component";

import AddIcon from "@material-ui/icons/Add";
import { Button } from "@material-ui/core";

import ContentStudioEquipmentStore from "stores/ContentStudioEquipmentStore";

import Page from "components/Layout/Page";
import ConfirmationDialog from "components/ConfirmationDialog";
import SortingControl from "components/SortingControl";
import withGlobalPopup, {
  GlobalPopupProps,
} from "components/Popup/withGlobalPopup";

import RestoreArchivedButton from "components/RestoreArchivedButton";
import Placeholder from "components/Placeholder";

import NavBar from "../NavBar";
import EquipmentCard from "./EquipmentCard";
import EquipmentForm from "./EquipmentForm";

import { SNACKBAR_TYPES } from "components/Snackbar";

import {
  withGlobalSnackbar,
  SnackbarContextProps,
} from "services/snackbar/snackbarContext";

import { TableSortType } from "stores/templates/TableStore";

import {
  PageControl,
  SearchContainer,
  SearchControls,
  SearchControlWrapper,
  Search,
  EquipmentCardWrapper,
  ScrollLoader,
} from "./styles";

type Props = RouteComponentProps & GlobalPopupProps & SnackbarContextProps & {};

const HEADER_WITH_TITLE_HEIGHT = "338px";

@observer
class Equipment extends React.Component<Props> {
  @observable isPopupDirty: boolean = false;

  @computed get equipments() {
    return ContentStudioEquipmentStore.items;
  }

  @computed get equipmentListIsEmpty() {
    return this.equipments.length === 0;
  }

  componentDidMount() {
    ContentStudioEquipmentStore.fetch();
  }

  componentWillUnmount() {
    ContentStudioEquipmentStore.reset();
  }

  onChangeIsPopupDirty = ({ dirty }: { dirty: boolean }) => {
    this.isPopupDirty = dirty;
  };

  fetchMoreEquipments = async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioEquipmentStore.fetchMoreEquipments();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onSearchApply = (value: string) => {
    ContentStudioEquipmentStore.applySearch(value);
    ContentStudioEquipmentStore.fetch();
  };

  onSearchClear = () => {
    ContentStudioEquipmentStore.clearSearch();
    ContentStudioEquipmentStore.fetch();
  };

  onSortApply = (value: TableSortType) => {
    ContentStudioEquipmentStore.applyOrder(value);
    ContentStudioEquipmentStore.resetPage();
    ContentStudioEquipmentStore.fetch();
  };

  onManageEquipment = (initialValues?: any) => {
    const { openPopup } = this.props;

    openPopup({
      key: "ManageEquipment",
      canCloseByOuterClick: false,
      withoutExitButton: true,
      confirmation: {
        renderConfirmation: ({ deny, allow }) => (
          <ConfirmationDialog
            title="Discard Changes"
            text="Are you sure you would like to discard changes? All information will be lost."
            declineText="Cancel"
            acceptText="Discard"
            onDecline={deny}
            onAccept={allow}
          />
        ),
        shouldShowConfirmation: this.shouldShowConfirmation,
      },
      render: ({ close }) => (
        <EquipmentForm
          isLoading={ContentStudioEquipmentStore.isEquipmentManaging}
          initialValues={initialValues}
          onSubmit={this.onManageEquipmentSubmit(close)}
          onClose={close}
          onChangeIsDirty={this.onChangeIsPopupDirty}
        />
      ),
    });
  };

  onManageEquipmentSubmit = (close: () => void) => async (values: any) => {
    const { setMessage } = this.props;
    const { id, file, title } = values;

    const data = { title: title?.trim() };

    try {
      if (!id) {
        await ContentStudioEquipmentStore.createEquipment({
          file,
          ...data,
        });
      } else {
        const payload = { id, ...data };
        await ContentStudioEquipmentStore.editEquipmentInfo(payload);

        if (typeof file !== "string") {
          const payload = { file, id };
          await ContentStudioEquipmentStore.editEquipmentImage(payload);
        }
      }

      this.resetIsPopupDirty();
      close();
      ContentStudioEquipmentStore.resetPage();
      ContentStudioEquipmentStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  onAddMovement = () => {
    this.onManageEquipment();
  };

  onEditMovement = (equipment: any) => () => {
    const { formData } = equipment;

    this.onManageEquipment(formData);
  };

  onArchive = (equipment: any) => () => {
    const { openPopup } = this.props;

    const equipmentName = equipment.title;
    const equipmentId = equipment.id;

    openPopup({
      key: "ArchiveEquipment",
      canCloseByOuterClick: true,
      withoutExitButton: true,
      render: ({ close }) => (
        <ConfirmationDialog
          title={"Archive Equipment"}
          text={`Are you sure you want to archive ${equipmentName} equipment? It will no longer be displayed to user in the app and moved to the Archive in the Admin panel.`}
          acceptText={"Archive"}
          declineText={"Cancel"}
          isLoading={ContentStudioEquipmentStore.isEquipmentManaging}
          onDecline={close}
          onAccept={this.archiveEquipment(equipmentId, close)}
        />
      ),
    });
  };

  archiveEquipment = (id: number, close: () => void) => async () => {
    const { setMessage } = this.props;

    try {
      await ContentStudioEquipmentStore.archiveEquipment(id);
      close();
      ContentStudioEquipmentStore.resetPage();
      ContentStudioEquipmentStore.fetch();
    } catch (error) {
      setMessage(error?.message, SNACKBAR_TYPES.ERROR);
    }
  };

  resetIsPopupDirty = () => {
    this.isPopupDirty = false;
  };

  shouldShowConfirmation = () => {
    return this.isPopupDirty;
  };

  render() {
    return (
      <Page
        title="Equipment"
        topControls={<NavBar />}
        controls={
          <>
            <PageControl>
              <RestoreArchivedButton />
            </PageControl>
            <PageControl>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={this.onAddMovement}
              >
                Add Equipment
              </Button>
            </PageControl>
          </>
        }
        isLoading={ContentStudioEquipmentStore.isLoading}
      >
        <SearchContainer>
          <Search
            initValue={ContentStudioEquipmentStore.search}
            onApply={this.onSearchApply}
            onClear={this.onSearchClear}
          />
          <SearchControls>
            <SearchControlWrapper>
              <SortingControl
                initValue={
                  ContentStudioEquipmentStore.orderContext.appliedOrderInfo
                }
                onApply={this.onSortApply}
              />
            </SearchControlWrapper>
          </SearchControls>
        </SearchContainer>

        {!this.equipmentListIsEmpty ? (
          <InfiniteScroll
            dataLength={this.equipments.length}
            hasMore={ContentStudioEquipmentStore.hasMoreInfo}
            next={this.fetchMoreEquipments}
            height={`calc(100vh - ${HEADER_WITH_TITLE_HEIGHT})`}
            loader={<ScrollLoader />}
          >
            {this.equipments.map(equipment => (
              <EquipmentCardWrapper key={equipment.id}>
                <EquipmentCard
                  title={equipment.title}
                  image={equipment.imageUrl}
                  onArchive={this.onArchive(equipment)}
                  onEdit={this.onEditMovement(equipment)}
                />
              </EquipmentCardWrapper>
            ))}
          </InfiniteScroll>
        ) : (
          <Placeholder
            height={280}
            width={280}
            text={
              ContentStudioEquipmentStore.search
                ? "No matches found. Please try another search."
                : "No equipment added yet."
            }
          />
        )}
      </Page>
    );
  }
}

export default withGlobalPopup(withGlobalSnackbar(withRouter(Equipment)));
